import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import {
  FeedbackRequestDto,
  FeedbackMode,
  FeedbackRequestStatus,
} from "../../../types/feedback/FeedbackRequestDto";
import CoContributorNoteDto from "../../../types/feedback/CoContributorNoteDto";
import feedbackRequestApi from "../../../api/feedback/feedbackRequestApi";
import { AlertPopup, Tooltip } from "../../common";
import { FormattedDate } from "../../common";
import SmallLoader from "../../loaders/SmallLoader";
import FeedbackReplyPreview from "./FeedbackReplyPreview";
import {
  isActiveFeedbackRequestStatus,
  normalizeFeedbackMode,
  normalizeFeedbackRequestStatus,
} from "../../../helpers/feedbackModeHelper";

interface FeedbackRequestListProps {
  requests: FeedbackRequestDto[];
  notes: CoContributorNoteDto[];
  isLoading: boolean;
  api: feedbackRequestApi;
  onRequestDeleted: () => void;
}

function FeedbackRequestList({
  requests,
  notes,
  isLoading,
  api,
  onRequestDeleted,
}: FeedbackRequestListProps) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState<number | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedRequestIds, setExpandedRequestIds] = useState<Set<number>>(
    new Set()
  );

  const notesByRequestId = useMemo(() => {
    const map = new Map<number, CoContributorNoteDto[]>();
    for (const note of notes) {
      const existing = map.get(note.feedbackRequestId) ?? [];
      existing.push(note);
      map.set(note.feedbackRequestId, existing);
    }
    map.forEach((requestNotes) => {
      requestNotes.sort(
        (a, b) =>
          new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
      );
    });
    return map;
  }, [notes]);

  if (isLoading) {
    return <SmallLoader />;
  }

  const activeRequests = requests.filter((r) =>
    isActiveFeedbackRequestStatus(r.status)
  );

  const showModeBadge =
    new Set(
      activeRequests
        .map((r) => normalizeFeedbackMode(r.modeAtCreation))
        .filter((m) => m !== "NONE" && m !== "SUBJECTEMPLOYEECHOICE")
    ).size > 1;

  if (activeRequests.length === 0) {
    return null;
  }

  const handleDeleteClick = (requestId: number) => {
    setDeletingRequestId(requestId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deletingRequestId) return;
    setIsDeleting(true);

    api.deleteRequest(
      deletingRequestId,
      () => {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
        setDeletingRequestId(null);
        onRequestDeleted();
      },
      () => {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
        setDeletingRequestId(null);
      }
    );
  };

  const toggleRequestExpanded = (requestId: number) => {
    setExpandedRequestIds((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const getStatusBadge = (status: FeedbackRequestStatus) => {
    switch (status) {
      case "PENDING":
        return (
          <Tooltip
            triggerElement={
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 cursor-help">
                Pending
              </span>
            }
            content={t("Feedback.CollabDoc.StatusPendingTooltip", {
              defaultValue:
                "Waiting for the recipient to submit their feedback.",
            })}
          />
        );
      case "SUBMITTED":
        return (
          <Tooltip
            triggerElement={
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 cursor-help">
                {t("Feedback.CollabDoc.StatusFeedbackReceived", {
                  defaultValue: "Feedback received",
                })}
              </span>
            }
            content={t("Feedback.CollabDoc.StatusFeedbackReceivedTooltip", {
              defaultValue: "The recipient has submitted their feedback.",
            })}
          />
        );
      default:
        return null;
    }
  };

  const getModeBadge = (mode: FeedbackMode) => {
    if (mode === "FULLREVIEWACCESS") {
      return (
        <Tooltip
          triggerElement={
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 cursor-help">
              {t("Feedback.CollabDoc.ModeReviewAndFeedback", {
                defaultValue: "Review + Feedback",
              })}
            </span>
          }
          content={t("Feedback.CollabDoc.ModeReviewAndFeedbackTooltip", {
            defaultValue:
              "The recipient can view this document in read-only mode and leave feedback notes.",
          })}
        />
      );
    }
    return (
      <Tooltip
        triggerElement={
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 cursor-help">
            {t("Feedback.CollabDoc.ModeFeedbackOnly", {
              defaultValue: "Feedback only",
            })}
          </span>
        }
        content={t("Feedback.CollabDoc.ModeFeedbackOnlyTooltip", {
          defaultValue:
            "The recipient submits feedback from their dashboard without access to this document.",
        })}
      />
    );
  };

  const deletingRequest = deletingRequestId
    ? requests.find((r) => r.id === deletingRequestId)
    : undefined;
  const deletingRequestHasDocAccess =
    deletingRequest !== undefined &&
    normalizeFeedbackMode(deletingRequest.modeAtCreation) ===
      "FULLREVIEWACCESS";
  const deletingRequestHasNotes =
    deletingRequest !== undefined &&
    ((deletingRequest.noteCount ?? 0) > 0 ||
      (notesByRequestId.get(deletingRequest.id)?.length ?? 0) > 0);

  const recipientName = deletingRequest?.recipientName ?? "Unknown";

  const deleteConfirmCopy = (() => {
    if (deletingRequestHasNotes) {
      return {
        title: t("Feedback.CollabDoc.DeleteFeedbackTitle", {
          defaultValue: "Delete feedback",
        }),
        bodyText: t("Feedback.CollabDoc.DeleteFeedbackBody", {
          defaultValue:
            "{{recipientName}}'s feedback will be deleted. This action cannot be undone. Are you sure you want to do this?",
          recipientName,
        }),
        primaryButtonText: isDeleting
          ? t("Feedback.CollabDoc.DeletingFeedback", {
              defaultValue: "Deleting...",
            })
          : t("Feedback.CollabDoc.DeleteFeedbackConfirm", {
              defaultValue: "Yes, delete",
            }),
        secondaryButtonText: t("Feedback.CollabDoc.DeleteFeedbackCancel", {
          defaultValue: "No, cancel",
        }),
      };
    }

    if (deletingRequestHasDocAccess) {
      return {
        title: t("Feedback.CollabDoc.WithdrawFeedbackRequestTitle", {
          defaultValue: "Withdraw feedback request",
        }),
        bodyText: t("Feedback.CollabDoc.WithdrawFeedbackRequestWithAccessBody", {
          defaultValue:
            "{{recipientName}} will no longer be able to view this document to give feedback.",
          recipientName,
        }),
        primaryButtonText: isDeleting
          ? t("Feedback.CollabDoc.WithdrawingFeedbackRequest", {
              defaultValue: "Withdrawing...",
            })
          : t("Feedback.CollabDoc.WithdrawFeedbackRequestConfirm", {
              defaultValue: "Withdraw request",
            }),
        secondaryButtonText: t("Feedback.CollabDoc.KeepFeedbackRequest", {
          defaultValue: "Keep request",
        }),
      };
    }

    return {
      title: t("Feedback.CollabDoc.WithdrawFeedbackRequestTitle", {
        defaultValue: "Withdraw feedback request",
      }),
      bodyText: t("Feedback.CollabDoc.WithdrawFeedbackRequestWithoutAccessBody", {
        defaultValue: "{{recipientName}} will no longer be able to give feedback.",
        recipientName,
      }),
      primaryButtonText: isDeleting
        ? t("Feedback.CollabDoc.WithdrawingFeedbackRequest", {
            defaultValue: "Withdrawing...",
          })
        : t("Feedback.CollabDoc.WithdrawFeedbackRequestConfirm", {
            defaultValue: "Withdraw request",
          }),
      secondaryButtonText: t("Feedback.CollabDoc.KeepFeedbackRequest", {
        defaultValue: "Keep request",
      }),
    };
  })();

  return (
    <div className="space-y-2">
      {activeRequests.map((request) => {
        const requestNotes = notesByRequestId.get(request.id) ?? [];
        const requestStatus = normalizeFeedbackRequestStatus(request.status);
        const requestMode = normalizeFeedbackMode(request.modeAtCreation);
        const hasReply =
          requestStatus === "SUBMITTED" && requestNotes.length > 0;

        return (
          <div
            key={request.id}
            className="bg-white border border-gray-200 rounded-md p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="text-sm font-medium text-gray-800">
                  {request.recipientName ?? "Unknown"}
                </span>
                {getStatusBadge(requestStatus)}
                {showModeBadge && getModeBadge(requestMode)}
                {request.noteCount > 1 && (
                  <span className="text-xs text-gray-500">
                    {request.noteCount} notes
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-gray-400">
                  <FormattedDate
                    date={new Date(request.dateCreated)}
                    displayMode="FROM-NOW"
                    convertFromUtc
                  />
                </span>
                <button
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  onClick={() => handleDeleteClick(request.id)}
                  title="Delete request"
                >
                  <FontAwesomeIcon icon={faTrashCan} className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            {request.message && (
              <p className="text-sm text-gray-500 mt-1.5 italic">
                &ldquo;{request.message}&rdquo;
              </p>
            )}
            {hasReply && (
              <FeedbackReplyPreview
                notes={requestNotes}
                isExpanded={expandedRequestIds.has(request.id)}
                onToggle={() => toggleRequestExpanded(request.id)}
              />
            )}
          </div>
        );
      })}

      <AlertPopup
        isOpen={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onPrimaryButtonClick={confirmDelete}
        onSecondaryButtonClick={() => {
          setShowDeleteConfirm(false);
          setDeletingRequestId(null);
        }}
        primaryButtonText={deleteConfirmCopy.primaryButtonText}
        secondaryButtonText={deleteConfirmCopy.secondaryButtonText}
        bodyText={deleteConfirmCopy.bodyText}
        title={deleteConfirmCopy.title}
      />
    </div>
  );
}

export default FeedbackRequestList;
